/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Logger } from '@/shared/logger/logger';

/**
 * Right rotate a 32-bit number by `amount` bits.
 */
function rightRotate(value: number, amount: number): number {
  return ((value >>> amount) | (value << (32 - amount))) >>> 0;
}

/**
 * Computes SHA-256 hash of a given string.
 *
 * @param message - The input string to be hashed.
 * @returns Hexadecimal string of the 256-bit hash.
 */
export async function SHA256(
  message: string | null | undefined,
): Promise<string> {
  if (message === null || message === undefined) {
    return '';
  }

  try {
    const msgUint8 = new TextEncoder().encode(message);

    // SHA-256 round constants:
    const K = new Uint32Array([
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      0x42_8a_2f_98, 0x71_37_44_91, 0xb5_c0_fb_cf, 0xe9_b5_db_a5, 0x39_56_c2_5b,
      0x59_f1_11_f1,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      0x92_3f_82_a4, 0xab_1c_5e_d5, 0xd8_07_aa_98, 0x12_83_5b_01, 0x24_31_85_be,
      0x55_0c_7d_c3,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      0x72_be_5d_74, 0x80_de_b1_fe, 0x9b_dc_06_a7, 0xc1_9b_f1_74, 0xe4_9b_69_c1,
      0xef_be_47_86, 0x0f_c1_9d_c6, 0x24_0c_a1_cc, 0x2d_e9_2c_6f, 0x4a_74_84_aa,
      0x5c_b0_a9_dc, 0x76_f9_88_da,
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      0x98_3e_51_52, 0xa8_31_c6_6d, 0xb0_03_27_c8, 0xbf_59_7f_c7, 0xc6_e0_0b_f3,
      0xd5_a7_91_47, 0x06_ca_63_51, 0x14_29_29_67, 0x27_b7_0a_85, 0x2e_1b_21_38,
      0x4d_2c_6d_fc, 0x53_38_0d_13, 0x65_0a_73_54, 0x76_6a_0a_bb, 0x81_c2_c9_2e,
      0x92_72_2c_85, 0xa2_bf_e8_a1, 0xa8_1a_66_4b, 0xc2_4b_8b_70, 0xc7_6c_51_a3,
      0xd1_92_e8_19, 0xd6_99_06_24, 0xf4_0e_35_85, 0x10_6a_a0_70, 0x19_a4_c1_16,
      0x1e_37_6c_08, 0x27_48_77_4c, 0x34_b0_bc_b5, 0x39_1c_0c_b3, 0x4e_d8_aa_4a,
      0x5b_9c_ca_4f, 0x68_2e_6f_f3, 0x74_8f_82_ee, 0x78_a5_63_6f, 0x84_c8_78_14,
      0x8c_c7_02_08, 0x90_be_ff_fa, 0xa4_50_6c_eb, 0xbe_f9_a3_f7, 0xc6_71_78_f2,
    ]);

    // Initial hash values (H0 ... H7):
    let h0 = 0x6a_09_e6_67;
    let h1 = 0xbb_67_ae_85;
    let h2 = 0x3c_6e_f3_72;
    let h3 = 0xa5_4f_f5_3a;
    let h4 = 0x51_0e_52_7f;
    let h5 = 0x9b_05_68_8c;
    let h6 = 0x1f_83_d9_ab;
    let h7 = 0x5b_e0_cd_19;

    // Calculate padded message length (in bytes).
    // Block size is 512 bits = 64 bytes. Need:
    //   message data + 1 byte (0x80) + 8 bytes (message length).
    const origByteLen = msgUint8.length;
    const totalLenWithPadding = origByteLen + 1 + 8; // +1 for 0x80, +8 for length
    const padLength = Math.ceil(totalLenWithPadding / 64) * 64; // round up to multiple of 64

    const paddedMsg = new Uint8Array(padLength);
    paddedMsg.set(msgUint8);
    // Append the single 0x80 bit.
    paddedMsg[origByteLen] = 0x80;

    // Write 64-bit message length in bits, big-endian, at the end.
    // For extremely large messages > 2^32 bits, we split high / low 32 bits:
    const bitLenHi = Math.floor((origByteLen * 8) / 0x1_00_00_00_00);
    const bitLenLo = (origByteLen * 8) >>> 0;

    const view = new DataView(paddedMsg.buffer);
    view.setUint32(padLength - 8, bitLenHi, false); // high 32 bits (big-endian)
    view.setUint32(padLength - 4, bitLenLo, false); // low 32 bits  (big-endian)

    // Process the message in successive 512-bit (64-byte) chunks
    for (let i = 0; i < paddedMsg.length; i += 64) {
      const w = new Uint32Array(64);

      // Break chunk into sixteen 32-bit big-endian words w[0..15]
      for (let j = 0; j < 16; j++) {
        w[j] = view.getUint32(i + j * 4, false); // big-endian
      }

      // Extend the first 16 words into the remaining 48 words w[16..63]
      for (let j = 16; j < 64; j++) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const wPrev15 = w[j - 15]!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const wPrev2 = w[j - 2]!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const wPrev16 = w[j - 16]!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const wPrev7 = w[j - 7]!;

        const s0 =
          rightRotate(wPrev15, 7) ^ rightRotate(wPrev15, 18) ^ (wPrev15 >>> 3);
        const s1 =
          rightRotate(wPrev2, 17) ^ rightRotate(wPrev2, 19) ^ (wPrev2 >>> 10);
        w[j] = (wPrev16 + s0 + wPrev7 + s1) >>> 0;
      }

      // Initialize working variables to current hash value
      let a = h0;
      let b = h1;
      let c = h2;
      let d = h3;
      let e = h4;
      let f = h5;
      let g = h6;
      let hh = h7;

      // Compression function main loop
      for (let j = 0; j < 64; j++) {
        const S1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
        const ch = (e & f) ^ (~e & g);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const kVal = K[j]!;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const wVal = w[j]!;
        const temp1 = (hh + S1 + ch + kVal + wVal) >>> 0;

        const S0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
        const maj = (a & b) ^ (a & c) ^ (b & c);
        const temp2 = (S0 + maj) >>> 0;

        hh = g;
        g = f;
        f = e;
        e = (d + temp1) >>> 0;
        d = c;
        c = b;
        b = a;
        a = (temp1 + temp2) >>> 0;
      }

      // Add the compressed chunk to the current hash value
      h0 = (h0 + a) >>> 0;
      h1 = (h1 + b) >>> 0;
      h2 = (h2 + c) >>> 0;
      h3 = (h3 + d) >>> 0;
      h4 = (h4 + e) >>> 0;
      h5 = (h5 + f) >>> 0;
      h6 = (h6 + g) >>> 0;
      h7 = (h7 + hh) >>> 0;
    }

    // Produce the final hash value (big-endian) as a 64-character hex string
    return [h0, h1, h2, h3, h4, h5, h6, h7]
      .map(hVal => hVal.toString(16).padStart(8, '0'))
      .join('');
  } catch (error) {
    Logger.error('Error in SHA256 hashing:', error);
    return '';
  }
}
